/* global AudioContext, performance, TimeoutId */
// https://en.wikipedia.org/wiki/ITU-R_468_noise_weighting
import iturWeight from 'a-weighting/m';
import EventMapEmitter, { EventHandlerMap } from '../../utils/EventMapEmitter';
import { ReadonlyAnalyserNode } from 'app/services/WebAudio';

interface IVolumeAnalyserInit {
  readonly probeTime: number;
  readonly threshold: number;
  readonly resolution: number;
  /**
   * Number of seconds that will be used to measure the time since last non-zero
   * to determine dead silence.
   */
  readonly silenceTimeSeconds: number;
  analyser?: ReadonlyAnalyserNode;
}

interface IVolumeAnalyserEventMap {
  volume: number;
  speaking: boolean;
}

export type VolumeAnalyserEventHandlerMap = EventHandlerMap<IVolumeAnalyserEventMap>;

/**
 * Volume analyser is detecting if some one is speaking (when the
 * volume is above certain level). Start/stops audio context depending
 * on presence / absence of media stream
 *
 * Emits events:
 * - `volume` with new values of volume
 * - `speaking`: boolean value if audio level is above threshold
 */
export default class VolumeAnalyser {
  private readonly probeTime: number;
  private readonly resolution: number;
  private readonly threshold: number;

  private analyser?: ReadonlyAnalyserNode;
  private data?: Uint8Array;
  private weights?: Float32Array;
  private sampleRate?: AudioContext['sampleRate'];
  private speaking: boolean;
  private volume: number;
  private timeout: TimeoutId;
  private lastNonZeroTotalAmplitudeTime?: number;

  private emitter: EventMapEmitter<IVolumeAnalyserEventMap>;

  public constructor({
    probeTime = 200,
    resolution = 10,
    threshold = 0.4,
    silenceTimeSeconds = 5,
    analyser,
  }: Partial<IVolumeAnalyserInit> = {}) {
    this.probeTime    = probeTime;
    this.resolution   = resolution;
    this.threshold    = threshold;
    this.speaking     = false;
    this.volume       = 0;
    this.timeout      = -1;

    this.analyse = this.analyse.bind(this);
    this.emitter = new EventMapEmitter();

    this.setAnalyser(analyser);
  }

  public get analyserNode() {
    return this.analyser;
  }

  public setAnalyser(analyser?: ReadonlyAnalyserNode) {
    if (this.analyser === analyser) {
      return;
    }

    if (this.analyser) {
      this.stop();
    }

    this.analyser = analyser;

    if (this.analyser) {
      this.start();
    }
  }

  public bindEvents(map: VolumeAnalyserEventHandlerMap) {
    this.emitter.bindEvents(map);
  }

  public unbindEvents(map: VolumeAnalyserEventHandlerMap) {
    this.emitter.unbindEvents(map);
  }

  private analyse() {
    if (!this.analyser || !this.data || !this.weights || !this.lastNonZeroTotalAmplitudeTime) {
      return;
    }

    this.analyser.getByteFrequencyData(this.data);

    if (this.data.reduce((a, b) => a + b) > 0) {
      this.lastNonZeroTotalAmplitudeTime = performance.now();
    }

    const volume = weightedLoudness(this.data, this.weights);
    const roundedVolume = Math.round(volume * this.resolution) / this.resolution;

    if (roundedVolume !== this.volume) {
      this.emitter.emit('volume', roundedVolume);
      this.volume = roundedVolume;
    }

    const speaking = roundedVolume > this.threshold;

    if (speaking !== this.speaking) {
      this.emitter.emit('speaking', speaking);
      this.speaking = speaking;
    }

    this.timeout = setTimeout(this.analyse, this.probeTime);
  }

  private start() {
    if (!this.analyser || !this.analyser.context) { return; }

    this.data = new Uint8Array(this.analyser.frequencyBinCount);
    this.weights = new Float32Array(this.analyser.frequencyBinCount);
    this.sampleRate = this.analyser.context.sampleRate;
    this.lastNonZeroTotalAmplitudeTime = performance.now();

    calculateLoudnessWeights(this.weights, this.sampleRate);

    this.analyse();
  }

  private stop() {
    this.emitter.emit('volume', 0);
    this.emitter.emit('speaking', false);

    clearTimeout(this.timeout);
  }
}

function calculateLoudnessWeights(weights: Float32Array, sampleRate: number): void {
  // calculate max to normalize levels
  let max = 0;

  for (let i = 0; i < weights.length; ++i) {
    const next = iturWeight(sampleRate * (i / weights.length));
    if (next > max) {
      max = next;
    }

    // eslint-disable-next-line no-param-reassign
    weights[i] = next;
  }

  for (let i = 0; i < weights.length; ++i) {
    // eslint-disable-next-line no-param-reassign
    weights[i] /= max;
  }
}

const weightedLoudness = (buffer: Uint8Array, weights: Float32Array) => {
  let value = 0;
  for (let i = 4; i < buffer.length - 6; ++i) {
    const next = (buffer[i] / 255) * weights[i];

    if (next > value) {
      value = next;
    }
  }

  return Math.min(1, value);
};
